export interface ScrollBox {
	scrollTop: number
	clientHeight: number
	scrollHeight: number
}

/**
 * Where the container has to sit for `element` to land in its middle.
 *
 * `scrollIntoView` walks every scrollable ancestor and the document, so a diff
 * centred inside the review pane also nudged the panes around it. This moves
 * exactly one box.
 */
export function centeredScrollTop({
	container,
	elementTop,
	elementHeight,
}: {
	container: ScrollBox
	/** The element's top, in the container's own scroll coordinates. */
	elementTop: number
	elementHeight: number
}): number {
	const target = elementTop - container.clientHeight / 2 + elementHeight / 2
	const max = Math.max(0, container.scrollHeight - container.clientHeight)
	return Math.min(Math.max(0, target), max)
}

/** True when the element is already comfortably inside the container's view. */
export function isComfortablyInView({
	container,
	elementTop,
	elementHeight,
}: {
	container: ScrollBox
	elementTop: number
	elementHeight: number
}): boolean {
	const top = elementTop - container.scrollTop
	const bottom = top + elementHeight
	return top >= 0 && bottom <= container.clientHeight
}

/** Centres `element` inside `container` without moving anything else. */
export function scrollWithinContainer(
	container: HTMLElement,
	element: HTMLElement,
	behavior: ScrollBehavior = "smooth",
): void {
	const elementTop =
		element.getBoundingClientRect().top -
		container.getBoundingClientRect().top +
		container.scrollTop
	const elementHeight = element.getBoundingClientRect().height
	const box = {
		scrollTop: container.scrollTop,
		clientHeight: container.clientHeight,
		scrollHeight: container.scrollHeight,
	}
	if (isComfortablyInView({ container: box, elementTop, elementHeight })) return
	container.scrollTo({
		top: centeredScrollTop({ container: box, elementTop, elementHeight }),
		behavior,
	})
}
